import React, { useState } from 'react';
import { Badge, Drawer, Flex, Segmented, Spin } from 'antd';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button } from '@bettercomp/ui-components';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useMarkNotificationUnread, useNotifications } from 'service/notificationsService';
import { NotificationItem } from './NotificationItem';

export function NotificationsDrawer() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const { data: allNotifications = [], isLoading: allLoading } = useNotifications(false);
  const { data: unreadNotifications = [], isLoading: unreadLoading } = useNotifications(true);
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markUnread } = useMarkNotificationUnread();
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMarkAllNotificationsRead();

  const visibleNotifications = filter === 'unread' ? unreadNotifications : allNotifications;
  const isLoading = filter === 'unread' ? unreadLoading : allLoading;
  const unreadCount = unreadNotifications.length;

  return (
    <>
      <Badge size='small' count={unreadCount} data-testid="notifications-drawer-badge">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          style={{ background: 'transparent' }}
          data-testid="notifications-drawer-trigger"
        />
      </Badge>
      <Drawer
        title={
          <Flex justify="space-between" align="center">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Button
                size="small"
                type="link"
                loading={isMarkingAllRead}
                onClick={() => markAllRead()}
              >
                Mark all read
              </Button>
            )}
          </Flex>
        }
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        data-testid="notifications-drawer"
      >
        <Segmented
          block
          value={filter}
          onChange={(v) => setFilter(v as 'all' | 'unread')}
          options={[
            { label: `All (${allNotifications.length})`, value: 'all' },
            { label: `Unread (${unreadCount})`, value: 'unread' },
          ]}
          style={{ marginBottom: 12 }}
        />
        {isLoading && (
          <Flex justify="center" align="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        )}
        {!isLoading && visibleNotifications.length === 0 && (
          <Flex vertical align="center" justify="center" gap={8} style={{ padding: 24, textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 48 }} />
            <span>You&apos;re all caught up</span>
            <span>
              No new notifications. We&apos;ll let you know when reports, exports or reviews are ready.
            </span>
          </Flex>
        )}
        {!isLoading &&
          visibleNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={markRead}
              onMarkUnread={markUnread}
            />
          ))}
      </Drawer>
    </>
  );
}
